import { SpeechPage } from './sections/SpeechPage';
import { validateTeamWorkspaceSearch } from './sections/TeamWorkspacePanel';
import { TenantAccessPage } from './sections/TenantAccessPage';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { useAuth } from '@rudi/auth';
import { AppShell } from './components/AppShell';
import { LoginPage } from './components/LoginPage';
import { NotAuthorized } from './components/NotAuthorized';
import { TenantsPage } from './sections/TenantsPage';
import { TenantDetailPage } from './sections/TenantDetailPage';
import { TeamDetailPage } from './sections/TeamDetailPage';
import { TenantTeamsPage } from './sections/TenantTeamsPage';
import { ClinicalTrialsPage } from './sections/ClinicalTrialsPage';
import { UsersPage } from './sections/UsersPage';
import { UserDetailPage } from './sections/UserDetailPage';
import { FeaturesPage } from './sections/FeaturesPage';
import { FeedbackRoutePage, validateFeedbackSearch } from './sections/FeedbackPage';
import { SpendPage } from './sections/SpendPage';
import { ApiKeysPage } from './sections/ApiKeysPage';
import { ReplaysRoutePage, validateReplaysSearch } from './sections/ReplaysPage';
import { TracesPage } from './sections/TracesPage';
import { ChatTracesPage } from './sections/ChatTracesPage';
import { QrCodesPage } from './sections/QrCodesPage';
import { PerformancePage } from './sections/PerformancePage';
import { ClustersPage, validateClustersSearch } from './sections/ClustersPage';
import { UptimePage } from './sections/UptimePage';
import { ModelsPage } from './sections/ModelsPage';
import { AutomationModelsPage } from './sections/AutomationModelsPage';
import { ChatModelPage } from './sections/ChatModelPage';
import { DataQualityOverviewPage } from './sections/DataQualityOverviewPage';
import { OrganisationsPage } from './sections/OrganisationsPage';
import {
  CatalogSiteRoutePage,
  DepartmentRoutePage,
  OrganisationRoutePage,
} from './sections/OrganisationDetailPage';
import { InvestigatorsPage } from './sections/InvestigatorsPage';
import { InvestigatorRoutePage } from './sections/InvestigatorDetailPage';
import { ReviewQueuePage } from './sections/ReviewQueuePage';
import { FindingsPage } from './sections/FindingsPage';
import { PracticeAgentsPage } from './sections/PracticeAgentsPage';
import { PracticeAgentRoutePage } from './sections/PracticeAgentDetailPage';
import { PracticeAgentReleasesPage } from './sections/PracticeAgentReleasesPage';
import { PracticeAgentsOverduePage } from './sections/PracticeAgentsOverduePage';
import { LiveViewRoutePage, validateLiveViewSearch } from './sections/LiveViewPage';
import { PrescreeningRunsPage } from './sections/PrescreeningRunsPage';
import { PrescreeningRunRoutePage } from './sections/PrescreeningRunDetailPage';
import { PrescreeningQueriesPage } from './sections/PrescreeningQueriesPage';
import {
  PrescreeningQueryRoutePage,
  validatePrescreeningQuerySearch,
} from './sections/PrescreeningQueryDetailPage';
import { PrescreeningPracticesPage } from './sections/PrescreeningPracticesPage';
import { PrescreeningPracticeRoutePage } from './sections/PrescreeningPracticePage';

/** Admit super admins only. Tenant admins manage their organization in Rudi's settings. */
function RootGate() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!isAuthenticated || !user) return <LoginPage />;
  if (!user.isOwner) return <NotAuthorized />;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const rootRoute = createRootRoute({ component: RootGate });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tenants' });
  },
});

const tenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants',
  component: TenantsPage,
});

const tenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$tenantId',
  component: TenantDetailPage,
});

const tenantTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$tenantId/teams',
  component: TenantTeamsPage,
});

const teamDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$tenantId/teams/$teamId',
  component: TeamDetailPage,
  validateSearch: validateTeamWorkspaceSearch,
});

const pendingTenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pending-tenants',
  beforeLoad: () => {
    throw redirect({ to: '/tenants' });
  },
});

const signupAttemptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup-attempts',
  beforeLoad: () => {
    throw redirect({ to: '/users' });
  },
});

const clinicalTrialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clinical-trials',
  component: ClinicalTrialsPage,
});

// Practice computers: the desktop apps in practice mode, through Rudi.
const practiceAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practice-agents',
  component: PracticeAgentsPage,
});

const practiceAgentReleasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practice-agents/releases',
  component: PracticeAgentReleasesPage,
});

const practiceAgentsOverdueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practice-agents/overdue',
  component: PracticeAgentsOverduePage,
});

const practiceAgentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practice-agents/$deviceId',
  component: PracticeAgentRoutePage,
});

// Pseudonymized rows and read-only SQL, end-to-end encrypted with the practice computer.
const practiceAgentLiveRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/practice-agents/$deviceId/live',
  component: LiveViewRoutePage,
  validateSearch: validateLiveViewSearch,
});

// Pre-screening: run aggregates, criteria queries and code sets, through Rudi.
const prescreeningRunsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening',
  component: PrescreeningRunsPage,
});

const prescreeningRunRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening/runs/$runId',
  component: PrescreeningRunRoutePage,
});

const prescreeningQueriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening/queries',
  component: PrescreeningQueriesPage,
});

const prescreeningQueryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening/queries/$queryId',
  component: PrescreeningQueryRoutePage,
  validateSearch: validatePrescreeningQuerySearch,
});

const prescreeningPracticesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening/practices',
  component: PrescreeningPracticesPage,
});

const prescreeningPracticeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prescreening/practices/$practiceId',
  component: PrescreeningPracticeRoutePage,
});

// Medical data quality: the Medical Repository's identity resolution, through Rudi.
const dataQualityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality',
  component: DataQualityOverviewPage,
});

const organisationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/organisations',
  component: OrganisationsPage,
});

const organisationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/organisations/$entityId',
  component: OrganisationRoutePage,
});

const catalogSiteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/catalog-sites/$entityId',
  component: CatalogSiteRoutePage,
});

const departmentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/departments/$entityId',
  component: DepartmentRoutePage,
});

const investigatorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/investigators',
  component: InvestigatorsPage,
});

const investigatorDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/investigators/$entityId',
  component: InvestigatorRoutePage,
});

const reviewQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/review',
  component: ReviewQueuePage,
});

const findingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/data-quality/findings',
  component: FindingsPage,
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: UsersPage,
});

const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$userId',
  component: UserDetailPage,
});

const featuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/features',
  component: FeaturesPage,
});

const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/models',
  component: ModelsPage,
});

const automationModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/automation-models',
  component: AutomationModelsPage,
});

const speechRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/speech',
  validateSearch: (search: Record<string, unknown>): { application?: 'rudi' | 'chat' } => ({
    application:
      search.application === 'chat' ? 'chat' : search.application === 'rudi' ? 'rudi' : undefined,
  }),
  component: SpeechPage,
});

const chatModelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat-model',
  component: ChatModelPage,
});

const feedbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/feedback',
  component: FeedbackRoutePage,
  validateSearch: validateFeedbackSearch,
});

const replaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/replays',
  component: ReplaysRoutePage,
  validateSearch: validateReplaysSearch,
});

const tracesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/traces',
  component: TracesPage,
});

const chatTracesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat-traces',
  component: ChatTracesPage,
});

const qrCodesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qr-codes',
  component: QrCodesPage,
});

const clustersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clusters',
  validateSearch: validateClustersSearch,
  component: ClustersPage,
});

const uptimeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/uptime',
  component: UptimePage,
});

const performanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/performance',
  component: PerformancePage,
});

// Old bookmark compat: the flags page became the features page.
const flagsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/flags',
  beforeLoad: () => {
    throw redirect({ to: '/features' });
  },
});

const spendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spend',
  component: SpendPage,
});

const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-keys',
  component: ApiKeysPage,
});

const tenantAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$tenantId/access',
  component: TenantAccessPage,
});

const routeTree = rootRoute.addChildren([
  tenantAccessRoute,
  indexRoute,
  tenantsRoute,
  tenantDetailRoute,
  tenantTeamsRoute,
  teamDetailRoute,
  pendingTenantsRoute,
  signupAttemptsRoute,
  clinicalTrialsRoute,
  practiceAgentsRoute,
  practiceAgentReleasesRoute,
  practiceAgentsOverdueRoute,
  practiceAgentDetailRoute,
  practiceAgentLiveRoute,
  prescreeningRunsRoute,
  prescreeningRunRoute,
  prescreeningQueriesRoute,
  prescreeningQueryRoute,
  prescreeningPracticesRoute,
  prescreeningPracticeRoute,
  dataQualityRoute,
  organisationsRoute,
  organisationDetailRoute,
  catalogSiteDetailRoute,
  departmentDetailRoute,
  investigatorsRoute,
  investigatorDetailRoute,
  reviewQueueRoute,
  findingsRoute,
  usersRoute,
  userDetailRoute,
  featuresRoute,
  modelsRoute,
  automationModelsRoute,
  chatModelRoute,
  speechRoute,
  feedbackRoute,
  replaysRoute,
  performanceRoute,
  clustersRoute,
  uptimeRoute,
  tracesRoute,
  chatTracesRoute,
  qrCodesRoute,
  flagsRedirectRoute,
  spendRoute,
  apiKeysRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
